/**
 * Billing > Payments (Phase 21 / Task 21.7d): every payment and reversal recorded against
 * Paelode's invoices, newest first. GET /v1/platform/billing/payments pages by cursor; the totals
 * line is the server's SUM over the whole filter (never a sum of the rows on screen).
 */
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { listFrom, useUrlParam } from '../../lib/url-state';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  paymentsQuery,
  useBillingCustomers,
  usePayments,
  type PaymentListRow,
} from './billing-hooks';
import { money } from './invoice-format';
import { PAYMENT_METHOD_LABELS } from './payment-format';
import { formatNumber } from '@lodex-pro/shared/number-format';

const cell: React.CSSProperties = { padding: '6px 8px' };

/** One fetched page; the next is asked for with its cursor. */
function PaymentsPageRows({
  query,
  onNext,
  isLast,
  first,
}: {
  query: string;
  onNext: (cursor: string) => void;
  isLast: boolean;
  first: boolean;
}): React.JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, isError } = usePayments(query);
  if (isLoading)
    return (
      <tr>
        <td colSpan={7} style={{ ...cell, ...subText }}>
          Loading…
        </td>
      </tr>
    );
  if (isError || data === undefined) {
    return (
      <tr>
        <td colSpan={7} role="alert" style={{ ...cell, color: 'var(--color-danger)' }}>
          Could not load the payments.
        </td>
      </tr>
    );
  }
  return (
    <>
      {first && (
        <tr data-testid="payments-totals" style={{ ...subText }}>
          <td colSpan={7} style={cell}>
            {formatNumber(data.totals.count)} entries · received {money(data.totals.paymentsCents)}{' '}
            · reversed {money(data.totals.reversalsCents)} · net {money(data.totals.netCents)}
          </td>
        </tr>
      )}
      {data.payments.map((p: PaymentListRow) => (
        <tr
          key={p.id}
          data-testid={`payment-row-${p.id}`}
          onClick={(): void => {
            void navigate(`/billing/invoices/${p.invoiceId}`, { state: listFrom(location) });
          }}
          style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border-primary)' }}
        >
          <td style={cell}>{p.paymentDate}</td>
          <td style={cell}>{p.customerName ?? p.customerTenantId}</td>
          <td style={cell}>{p.invoiceNumber ?? '—'}</td>
          <td style={cell}>
            {p.kind === 'REVERSAL' ? (
              <Pill tone="warning" label="Reversal" />
            ) : p.method === null ? (
              '—'
            ) : (
              PAYMENT_METHOD_LABELS[p.method]
            )}
          </td>
          <td style={cell}>{p.kind === 'REVERSAL' ? (p.reason ?? '') : (p.reference ?? '—')}</td>
          <td style={{ ...cell, textAlign: 'right' }}>
            {p.kind === 'REVERSAL' ? `−${money(p.amountCents)}` : money(p.amountCents)}
          </td>
          <td />
        </tr>
      ))}
      {isLast && data.nextCursor !== null && (
        <tr>
          <td colSpan={7} style={{ ...cell, textAlign: 'center' }}>
            <Button
              type="button"
              variant="outline"
              onClick={(): void => {
                if (data.nextCursor !== null) onNext(data.nextCursor);
              }}
            >
              Load more
            </Button>
          </td>
        </tr>
      )}
    </>
  );
}

export function PaymentsPage(): React.JSX.Element {
  // OPEN-191 — the filters live in the URL, so an invoice's Back returns to them. The cursor
  // pages stay in state: they belong to the filter (below), and a fresh visit starts at page 1.
  const [from, setFrom] = useUrlParam('from', '', []);
  const [to, setTo] = useUrlParam('to', '', []);
  const [customer, setCustomer] = useUrlParam('customer', '', []);
  // #974 review LOW-2: the pages belong to the filter they were fetched for, so a filter
  // change starts from the first page in the same render (no stale cursor is ever asked for).
  const filterKey = `${from}|${to}|${customer}`;
  const [pages, setPages] = React.useState<{ key: string; cursors: Array<string | null> }>({
    key: filterKey,
    cursors: [null],
  });
  const cursors = pages.key === filterKey ? pages.cursors : [null];
  const customers = useBillingCustomers();
  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'end', marginBottom: '12px' }}>
        <FormRow label="From" htmlFor="pay-from">
          <input
            id="pay-from"
            type="date"
            value={from}
            onChange={(e): void => setFrom(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="To" htmlFor="pay-to">
          <input
            id="pay-to"
            type="date"
            value={to}
            onChange={(e): void => setTo(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Customer" htmlFor="pay-customer">
          <select
            id="pay-customer"
            value={customer}
            onChange={(e): void => setCustomer(e.target.value)}
            style={fieldStyle}
          >
            <option value="">All customers</option>
            {(customers.data?.customers ?? []).map((c) => (
              <option key={c.tenantId} value={c.tenantId}>
                {c.tenantName}
              </option>
            ))}
          </select>
        </FormRow>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr style={{ textAlign: 'left', ...subText }}>
            <th style={cell}>Paid on</th>
            <th style={cell}>Customer</th>
            <th style={cell}>Invoice</th>
            <th style={cell}>Method</th>
            <th style={cell}>Reference or reason</th>
            <th style={{ ...cell, textAlign: 'right' }}>Amount</th>
            <th style={cell} />
          </tr>
        </thead>
        <tbody>
          {cursors.map((c, i) => (
            <PaymentsPageRows
              key={c ?? 'first'}
              query={paymentsQuery({ from, to, customer, cursor: c })}
              first={i === 0}
              isLast={i === cursors.length - 1}
              onNext={(next): void => setPages({ key: filterKey, cursors: [...cursors, next] })}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
